import { Trans } from "@lingui/macro";
import React, { useEffect, useState } from "react";
import { errorGenerator } from "model/system/utils";
import { Button } from "components/UI/Button/Button";
import { Loader } from "components/UI/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import DateFilter from "components/UI/DateFilter/DateFilter";
import { InputText } from "components/UI/InputText/InputText";
import { getStoreList } from "model/dashboardStores/dStoresAPI";
import { getSalesTransStatus, SALESTATUS } from "constants/salesStatus";
import { dSalesSetFiltersAC } from "model/dashboardSales/dSalesActionCreators";
import {
  NULL_VALUE,
  SelectBox,
  typeValuesListItem,
} from "components/UI/SelectBox/SelectBox";
import {
  saleTypes,
  IdSalesState,
  typeSalesFilters,
} from "model/dashboardSales/dSalesReducer";

export type typeDSalesFilters = {
  setOpenFilterBar: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActiveFilters: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DSalesFilters: React.FC<typeDSalesFilters> = ({
  setOpenFilterBar,
  setIsActiveFilters,
}) => {
  const dispatch = useDispatch();

  const filters = useSelector(
    (state: { dSales: IdSalesState }) => state.dSales.filters,
  );

  // store
  const [storeList, setStoreList] = useState<typeValuesListItem[]>([]);
  const [storeLoader, setStoreLoader] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(
    NULL_VALUE.id,
  );

  useEffect(() => {
    const setData = async () => {
      setStoreLoader(true);
      const res = await getStoreList({
        filters: {
          deleted: false,
          name: null,
        },
        full: false,
      });
      if (res.status === 200) {
        setStoreList([...storeList, ...res.data]);
      } else {
        errorGenerator(res);
      }
    };
    setData().finally(() => setStoreLoader(false));
    setSelectedStoreId(filters.storeId ? filters.storeId : NULL_VALUE.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payment type filter
  const [salePaymentType] = useState<typeValuesListItem[]>([
    {
      id: saleTypes.CASH,
      name: <Trans>Cash</Trans>,
    },
    {
      id: saleTypes.CARD,
      name: <Trans>Card</Trans>,
    },
    {
      id: saleTypes.CASHLESS,
      name: <Trans>Other non-cash</Trans>,
    },
    {
      id: saleTypes.QR,
      name: <Trans>QR-code</Trans>,
    },
  ]);
  const [salePaymentTypeId, setSalePaymentTypeId] = useState<string | null>(
    !filters.salePaymentType ? NULL_VALUE.id : filters.salePaymentType,
  );

  // Payment method SelectBox

  const paymentTypeList: typeValuesListItem[] = [
    { id: SALESTATUS.SALE, name: getSalesTransStatus(SALESTATUS.SALE) },
    { id: SALESTATUS.CREDIT, name: getSalesTransStatus(SALESTATUS.CREDIT) },
    { id: SALESTATUS.ADVANCE, name: getSalesTransStatus(SALESTATUS.ADVANCE) },
  ];
  const [paymentTypeValue, setPaymentTypeValue] = useState<string | null>(
    !filters.paymentStatus ? NULL_VALUE.id : filters.paymentStatus,
  );

  //cashierNameInput
  const [cashierNameValue, setCashierName] = useState(
    filters.cashierName || "",
  );
  const onChangeCashierName = (e: any) => {
    setCashierName(e.target.value);
  };

  //saleId Input
  const [saleIdValue, setSaleIdValue] = useState(filters.saleId || "");
  const onChangeSaleIdValue = (e: any) => {
    setSaleIdValue(e.target.value);
  };

  //saleNumberInput
  const [receiptNumberValue, setReceiptNumberValue] = useState(
    filters.receiptNumber || "",
  );
  const onChangeReceiptNumberValue = (e: any) => {
    setReceiptNumberValue(e.target.value);
  };

  // to Date
  const [toValue, setToValue] = useState(
    filters.date?.to ? filters.date.to.substring(0, 10) : "",
  );

  // from Date
  const [fromValue, setFromValue] = useState(
    filters.date?.from! ? filters.date.from.substring(0, 10) : "",
  );

  const checkIsChangedFiltersHandler = (
    newFilters: typeSalesFilters,
  ): boolean => {
    // eslint-disable-next-line eqeqeq
    return !(
      newFilters.cashierName === filters.cashierName &&
      newFilters.salePaymentType === filters.salePaymentType &&
      newFilters.saleId === filters.saleId &&
      newFilters.receiptNumber === filters.receiptNumber &&
      newFilters.date?.to === filters.date?.to &&
      newFilters.date?.from === filters.date?.from &&
      newFilters.paymentStatus === filters.paymentStatus &&
      newFilters.storeId === filters.storeId
    );
  };

  // Submit form
  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let a = fromValue ? new Date(fromValue).toISOString().slice(0, -1) : null;
    let b = toValue
      ? new Date(toValue).toISOString().slice(0, 11) + "23:59:59:99.999"
      : null;
    const newFilters: typeSalesFilters = {
      date:
        a === "" && b === ""
          ? null
          : {
              from: a,
              to: b,
            },
      cashierName:
        cashierNameValue.trim() === "" ? null : cashierNameValue.trim(),
      paymentStatus:
        paymentTypeValue === NULL_VALUE.id ? null : paymentTypeValue,
      salePaymentType:
        salePaymentTypeId === NULL_VALUE.id ? null : salePaymentTypeId,
      saleId: saleIdValue.trim() === "" ? null : saleIdValue.trim(),
      receiptNumber:
        receiptNumberValue.trim() === "" ? null : receiptNumberValue.trim(),
      storeId: selectedStoreId === NULL_VALUE.id ? null : selectedStoreId,
    };

    if (checkIsChangedFiltersHandler(newFilters)) {
      dispatch(dSalesSetFiltersAC(newFilters));
      setIsActiveFilters(true);
    }

    setOpenFilterBar(false);
  };

  const onRestoreHandler = () => {
    setToValue("");
    setFromValue("");
    setCashierName("");
    setSaleIdValue("");
    setReceiptNumberValue("");
    setPaymentTypeValue(NULL_VALUE.id);
    setSalePaymentTypeId(NULL_VALUE.id);
    setIsActiveFilters(false);
    setSelectedStoreId(NULL_VALUE.id);
    dispatch(
      dSalesSetFiltersAC({
        date: null,
        cashierName: null,
        saleId: null,
        receiptNumber: null,
        paymentStatus: null,
        salePaymentType: null,
        storeId: null,
      }),
    );
  };

  return (
    <>
      <form onSubmit={(e) => onSubmitHandler(e)}>
        <div className="flex flex-col gap-4">
          <div className={"relative"}>
            <SelectBox
              label={<Trans>Store</Trans>}
              valuesList={storeList}
              valueId={selectedStoreId}
              setIdValue={setSelectedStoreId}
            />

            {storeLoader && <Loader />}
          </div>

          <div>
            <SelectBox
              label={
                <Trans>
                  Payment status (all/full payment/credit/prepayment)
                </Trans>
              }
              valuesList={paymentTypeList}
              valueId={paymentTypeValue}
              setIdValue={setPaymentTypeValue}
            />
          </div>
          <div>
            <SelectBox
              label={<Trans>Payment method</Trans>}
              valuesList={salePaymentType}
              valueId={salePaymentTypeId}
              setIdValue={setSalePaymentTypeId}
            />
          </div>
          <div>
            <InputText
              id={"saleId"}
              label={<Trans>Sale ID</Trans>}
              onChange={onChangeSaleIdValue}
              value={saleIdValue}
              maxLength={100}
            />
          </div>
          <div>
            <InputText
              id={"saleId"}
              label={<Trans>Receipt number</Trans>}
              onChange={onChangeReceiptNumberValue}
              value={receiptNumberValue}
              maxLength={100}
              type={"number"}
              minValue={0}
            />
          </div>
          <div>
            <InputText
              id={"cashierName"}
              label={<Trans>Employee name</Trans>}
              onChange={onChangeCashierName}
              value={cashierNameValue}
              maxLength={100}
            />
          </div>
          <DateFilter
            label={<Trans>Date</Trans>}
            fromValue={fromValue}
            setFromValue={setFromValue}
            toValue={toValue}
            setToValue={setToValue}
            maxData={`${new Date().getUTCFullYear()}-${
              new Date().getUTCMonth() + 1 > 9
                ? new Date().getUTCMonth() + 1
                : "0" + (new Date().getUTCMonth() + 1)
            }-${new Date().getUTCDate()}`}
            minData={"2020-01-01"}
          />

          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <Button
              buttonClassName="w-full"
              secondary={true}
              type="button"
              label={<Trans>Clear</Trans>}
              onClick={onRestoreHandler}
            />
            <Button
              buttonClassName="w-full"
              type="submit"
              label={<Trans>Apply</Trans>}
            />
          </div>
        </div>
      </form>
    </>
  );
};
